import {
  DOMParser as PmDOMParser,
  DOMSerializer,
  type Node as PmNode,
} from "prosemirror-model";
import { blocksToTemplateInDom } from "@/shared/utils/variable-block";
import { pillsToTemplateInDom } from "@/shared/utils/variable-pill";
import { Transform } from "prosemirror-transform";
import {
  applyDerivedAttrs,
  capListDepth,
  stripRedundantTypographyMarks,
  stripTrailingBreaks,
} from "./normalize";
import { textSchema } from "./schema";
import { withLegacySizeMapping } from "./typography";
import { applyVariableTokens } from "./variables";

/**
 * Media and embedded content never enter the doc (spec §2.1 "scripts / unsafe
 * content", §5.3 external icons). Without an explicit ignore rule ProseMirror
 * would treat unknown elements as transparent and keep e.g. `<svg><text>`.
 */
const IGNORED_TAGS = [
  "img",
  "picture",
  "svg",
  "video",
  "audio",
  "canvas",
  "iframe",
  "object",
  "embed",
  "input",
  "button",
  "select",
  "textarea",
];

export const parser = new PmDOMParser(textSchema, [
  ...IGNORED_TAGS.map((tag) => ({ tag, ignore: true as const })),
  ...PmDOMParser.fromSchema(textSchema).rules,
]);
export const serializer = DOMSerializer.fromSchema(textSchema);

/**
 * Parse a stored HTML string into a doc. The schema drops anything it does
 * not model; the two normalizers only enforce invariants the schema cannot
 * express (one typography source per segment, list depth ≤ MAX_LIST_DEPTH). Nothing is
 * written back to the store unless the user actually edits.
 */
export const parseHtml = (html: string): PmNode => {
  const dom = new window.DOMParser().parseFromString(html ?? "", "text/html");
  // Existing content: raw px sizes count as a style only when they match the
  // design system exactly (Froala parity); the §3.5 band table is for paste.
  const doc = withLegacySizeMapping(() => parser.parse(dom.body));
  // An empty line is stored as Froala's `<p><br></p>`; that <br> is only
  // there so the line has height — the editor's empty block doesn't need it.
  const normalized = capListDepth(
    stripRedundantTypographyMarks(doc.copy(stripTrailingBreaks(doc.content))),
  );
  return applyDerivedAttrs(applyVariableTokens(new Transform(normalized))).doc;
};

/**
 * Serialize for storage. Highlights are stored with
 * `data-highlight-active="false"` so the reveal animation plays for readers
 * (the editor view renders "true" so the author sees the colour) — the same
 * flip Froala's save path performed.
 */
export const serializeHtml = (doc: PmNode): string => {
  const container = document.createElement("div");
  container.appendChild(serializer.serializeFragment(doc.content));
  for (const span of Array.from(
    container.querySelectorAll('span[data-highlight-active="true"]'),
  )) {
    span.setAttribute("data-highlight-active", "false");
  }
  // KPI counters are stored at their start value, inactive, so the reader
  // animates them (Froala's save path did the same).
  for (const span of Array.from(
    container.querySelectorAll("span[data-rkpi-active]"),
  )) {
    span.setAttribute("data-rkpi-active", "false");
    span.textContent = span.getAttribute("data-rkpi-number-from") ?? "0";
  }
  // ProseMirror needs a textblock inside every list item; storage keeps the
  // Froala shape `<li>text<ul>…</ul></li>`. Unwrap the item's single leading
  // paragraph when it carries no attributes of its own (the parser wraps it
  // back on load).
  for (const li of Array.from(container.querySelectorAll("li"))) {
    const first = li.firstElementChild;
    if (
      first?.tagName === "P" &&
      first.attributes.length === 0 &&
      Array.from(li.children).filter((c) => c.tagName === "P").length === 1
    ) {
      first.replaceWith(...Array.from(first.childNodes));
    }
  }
  // An empty block is stored with a <br> (Froala's `<p><br></p>`): an empty
  // <p> has no height, so a blank line the author left showed in the editor
  // and vanished from the read-only render and the viewer.
  for (const block of Array.from(
    container.querySelectorAll("p, h1, h2, h3, h4, li"),
  )) {
    if (block.childNodes.length === 0)
      block.appendChild(document.createElement("br"));
  }
  // Pills and version-mode blocks are stored as their `{{name}}` /
  // `{{{name}}}` tokens, formatting kept — the conversions Froala's save runs.
  pillsToTemplateInDom(container);
  blocksToTemplateInDom(container);
  return container.innerHTML;
};
